/**
 * A rating element's scale, and how a rating reads as text
 * (docs/plans/data-types-second-pass.md §3.1, decision D13).
 *
 *   max        options.max, a whole number of stars from 2 to 10, default 5.
 *              The legacy `count` spelling is still read, and a scale stored
 *              before the 2–10 rule is read as stored — the same lenient
 *              reading as keepr-api's utils/typedElements.js ratingScale, so
 *              the form draws the scale the API holds a value to.
 *   allowHalf  true: a value may be a multiple of 0.5.
 *
 * The API refuses a value past the scale (`range`) or between the steps
 * (`type`); utils/elementValidation.js says so on the field first.
 */

export const DEFAULT_RATING_MAX = 5;
export const RATING_MAX_MIN = 2;
export const RATING_MAX_MAX = 10;

/** The scales an author may pick, 2–10 stars. */
export const RATING_SCALE_CHOICES = Array.from({ length: RATING_MAX_MAX - RATING_MAX_MIN + 1 }, (_, i) => RATING_MAX_MIN + i);

/** { max, allowHalf } for an element's options (or an element). */
export function ratingScale(optionsOrElement) {
	const opts = optionsOrElement && optionsOrElement.options && typeof optionsOrElement.options === 'object'
		? optionsOrElement.options
		: (optionsOrElement || {});
	const raw = opts.max !== undefined && opts.max !== null && opts.max !== '' ? opts.max : opts.count;
	const max = Number(raw);
	return {
		max: Number.isFinite(max) && max > 0 ? max : DEFAULT_RATING_MAX,
		allowHalf: opts.allowHalf === true,
	};
}

/** The step a rating is entered on: 1, or 0.5 with half stars. */
export const ratingStep = options => (ratingScale(options).allowHalf ? 0.5 : 1);

/**
 * formatRating(value, options) -> string
 *
 * The stars a value earns, a "½" for a half star, and — on any scale but the
 * default five — the number out of the scale beside them ("★★★★★★★½ 7.5 / 10"),
 * because eight stars in a row do not read at a glance and the export already
 * prints "7.5 / 10". Zero on the default scale reads as nothing, as it always
 * has; a value that is not a number reads as it is stored.
 */
export function formatRating(value, options) {
	if (value === undefined || value === null || value === '') return '';
	const n = Number(value);
	if (!Number.isFinite(n)) return String(value);
	const { max } = ratingScale(options);
	const full = Math.max(0, Math.min(Math.floor(n), Math.ceil(max)));
	const half = n - Math.floor(n) >= 0.5 && full < max;
	const stars = `${'★'.repeat(full)}${half ? '½' : ''}`;
	if (max === DEFAULT_RATING_MAX) return stars;
	return stars ? `${stars} ${n} / ${max}` : `${n} / ${max}`;
}
